/**
 * Settings, devices, and the sync escape hatch.
 */

import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import { api, type User } from '../api/client'
import { CURSOR_KEY, db, getMeta, wipeLocal, type Exercise } from '../db'
import { refreshExercises, refreshPlans, sync, subscribe, type SyncState } from '../sync/engine'
import type { DistancePref, Unit } from '../lib/units'
import { OrientationLock } from '../components/OrientationLock'
import { RestTone } from '../components/RestTone'
import { RestWake } from '../components/RestWake'
import { UpdateCheck } from '../components/UpdateCheck'
import { parseIso } from '../lib/time'

/** The account-wide default. "auto" is what everybody had before this setting
 *  existed, so choosing nothing changes nothing. */
const DISTANCE_PREFS: { value: DistancePref; label: string }[] = [
  { value: 'auto', label: 'Automatic' },
  { value: 'metric', label: 'Metric (m / km)' },
  { value: 'imperial', label: 'Imperial (yd / mi)' },
]

type Device = {
  id: number
  name: string
  created_at: string | null
  last_used_at: string | null
  current: boolean
}

export function SettingsScreen({
  user,
  onUserChange,
  onSignOut,
}: {
  user: User
  onUserChange: (u: User) => void
  onSignOut: () => void
}) {
  const [devices, setDevices] = useState<Device[] | null>(null)
  const [state, setState] = useState<SyncState | null>(null)
  const [resetting, setResetting] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)

  const pending = useLiveQuery(() => db.outbox.count(), [], 0)
  const cursor = useLiveQuery(() => getMeta<number>(CURSOR_KEY, 0), [], 0)

  useEffect(() => subscribe(setState), [])

  useEffect(() => {
    api<{ devices: Device[] }>('/auth/devices')
      .then((r) => setDevices(r.devices))
      .catch(() => setDevices(null))
  }, [])

  async function patch(body: Partial<User>) {
    const res = await api<{ user: User }>('/auth/me', { method: 'PATCH', body })
    onUserChange(res.user)
  }

  return (
    <>
      <h1>Settings</h1>

      <div className="card">
        <div className="row">
          <div>
            <strong>{user.name}</strong>
            <div className="muted">@{user.username}</div>
          </div>
          <button className="secondary" style={{ marginTop: 0 }} onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>

      <h2>Units</h2>
      <div className="card">
        <div style={{ display: 'flex', gap: '.5rem' }}>
          {(['lb', 'kg'] as Unit[]).map((u) => (
            <button
              key={u}
              className={user.unit_pref === u ? '' : 'secondary'}
              style={{ marginTop: 0, flex: 1 }}
              onClick={() => patch({ unit_pref: u })}
            >
              {u === 'lb' ? 'Pounds' : 'Kilograms'}
            </button>
          ))}
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          Display only — everything is stored in kilograms, so switching never
          changes your history.
        </p>
      </div>

      {/* A separate setting, not a consequence of the one above. Deriving
          distance from the weight preference is what made pounds mean miles for
          every distance in the app, so an eighteen-metre farmer's carry read
          "0.01 mi" and had to be typed as 0.0112. */}
      <h2>Distance</h2>
      <div className="card">
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          {DISTANCE_PREFS.map((p) => (
            <button
              key={p.value}
              className={user.distance_pref === p.value ? '' : 'secondary'}
              style={{ marginTop: 0, flex: '1 1 6rem' }}
              onClick={() => patch({ distance_pref: p.value })}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          The default for new exercises. Short distances — carries, sprints,
          jumps — and long ones get separate units, because a farmer's carry and
          a 5 km row cannot sensibly share one. You can override any single
          exercise with the toggle beside its distance box while logging, and
          that choice sticks.
        </p>
      </div>

      <h2>Rest timer</h2>
      <div className="card">
        <label htmlFor="rest">Default rest (seconds)</label>
        <input
          id="rest"
          type="number"
          min={15}
          max={600}
          step={15}
          defaultValue={user.default_rest_seconds}
          onBlur={(e) => {
            const v = parseInt(e.target.value, 10)
            if (v && v !== user.default_rest_seconds) patch({ default_rest_seconds: v })
          }}
        />
        <RestWake />
        <RestTone />
      </div>

      <OrientationLock />

      <h2>Sync</h2>
      <div className="card">
        <ul className="plain" style={{ margin: 0 }}>
          <li>
            <span>Status</span>
            <span className="muted">
              {state?.running
                ? 'Syncing…'
                : state?.online === false
                  ? 'Offline — changes are saved here'
                  : state?.lastError
                    ? state.lastError
                    : 'Up to date'}
            </span>
          </li>
          <li>
            <span>Waiting to upload</span>
            <span className="muted">{pending} change{pending === 1 ? '' : 's'}</span>
          </li>
          <li>
            <span>Last synced</span>
            <span className="muted">
              {state?.lastSyncedAt
                ? new Date(state.lastSyncedAt).toLocaleTimeString([], {
                    hour: 'numeric', minute: '2-digit' })
                : 'never'}
            </span>
          </li>
          <li>
            <span>Cursor</span>
            <span className="muted">{cursor}</span>
          </li>
        </ul>

        <button style={{ width: '100%' }} onClick={() => sync({ reference: true })}>
          Sync now
        </button>
      </div>

      <h2>Devices</h2>
      <div className="card">
        {devices === null ? (
          <p className="muted" style={{ margin: 0 }}>
            Couldn't load — needs a connection.
          </p>
        ) : (
          <ul className="plain" style={{ margin: 0 }}>
            {devices.map((d) => (
              <li key={d.id}>
                <span>
                  {d.name}
                  {d.current && <span className="muted"> · this one</span>}
                </span>
                {!d.current && (
                  <button
                    className="secondary"
                    style={{ marginTop: 0, padding: '.2rem .5rem', minHeight: 0,
                             fontSize: '.8rem' }}
                    onClick={async () => {
                      await api(`/auth/devices/${d.id}`, { method: 'DELETE' })
                      setDevices((ds) => (ds ?? []).filter((x) => x.id !== d.id))
                    }}
                  >
                    Sign out
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="muted" style={{ marginBottom: 0 }}>
          Signing out a device revokes only its token. This is what to use if you
          lose a phone.
        </p>
      </div>

      <YourExercises userId={user.id} />

      <ArchivedPlans />

      <UpdateCheck />

      <h2>Troubleshooting</h2>
      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          Deletes everything stored on this device and downloads it again from
          the server. Nothing is lost as long as the counter above reads zero.
        </p>
        {confirmReset ? (
          <div className="row">
            <span className="muted">
              {pending > 0
                ? `${pending} change${pending === 1 ? '' : 's'} haven't uploaded yet — those will be lost.`
                : 'Everything is uploaded. Safe to reset.'}
            </span>
            <span>
              <button
                className="secondary"
                style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0 }}
                onClick={() => setConfirmReset(false)}
              >
                Cancel
              </button>
              <button
                disabled={resetting}
                style={{ marginTop: 0, marginLeft: '.4rem', padding: '.25rem .6rem',
                         minHeight: 0, background: 'var(--danger)',
                         borderColor: 'var(--danger)' }}
                onClick={async () => {
                  setResetting(true)
                  await wipeLocal()
                  await sync({ reference: true })
                  setResetting(false)
                  setConfirmReset(false)
                }}
              >
                {resetting ? 'Resetting…' : 'Reset'}
              </button>
            </span>
          </div>
        ) : (
          <button className="secondary" onClick={() => setConfirmReset(true)}>
            Reset local data
          </button>
        )}
      </div>
    </>
  )
}

/**
 * Archived plans, and getting one back.
 *
 * Archiving is deliberately not deletion — logged sessions point at plan days,
 * so a hard delete would strip "what was I following when I lifted this" out
 * of history that already happened. But until now archiving was a one-way
 * door: the plan vanished from the Plan tab with no way to see it again, which
 * makes archiving feel like a destructive act people avoid.
 *
 * It lives here rather than on the Plan tab because the common case is not
 * finding it. A list of things you've put away shouldn't compete for space
 * with the plan you're actually running.
 */
function ArchivedPlans() {
  const [plans, setPlans] = useState<ArchivedPlan[] | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = () => {
    api<{ plans: ArchivedPlan[] }>('/plans/archived')
      .then((r) => setPlans(r.plans))
      .catch(() => setPlans(null))
  }

  useEffect(load, [])

  // Nothing archived is the common case, and an empty card is just noise.
  if (plans !== null && plans.length === 0) return null

  return (
    <>
      <h2>Archived plans</h2>
      <div className="card">
        {plans === null ? (
          <p className="muted" style={{ margin: 0 }}>
            Couldn't load — needs a connection.
          </p>
        ) : (
          <ul className="plain" style={{ margin: 0 }}>
            {plans.map((p) => (
              <li key={p.id}>
                <span>
                  {p.name}
                  <div className="muted" style={{ fontSize: '.8rem' }}>
                    {p.days_per_week}× / week · archived {formatArchived(p.archived_at)}
                  </div>
                </span>
                <button
                  className="secondary"
                  disabled={busyId === p.id}
                  style={{ marginTop: 0, padding: '.2rem .5rem', minHeight: 0,
                           fontSize: '.8rem' }}
                  onClick={async () => {
                    setBusyId(p.id)
                    try {
                      await api(`/plans/${p.id}/restore`, { method: 'POST' })
                      setPlans((ps) => (ps ?? []).filter((x) => x.id !== p.id))
                      // The Plan tab and the Log screen both read the Dexie
                      // mirror, so a restore that didn't refresh it would leave
                      // the plan invisible until the next full sync.
                      await refreshPlans()
                    } finally {
                      setBusyId(null)
                    }
                  }}
                >
                  {busyId === p.id ? 'Restoring…' : 'Restore'}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="muted" style={{ marginBottom: 0 }}>
          Restoring puts a plan back on the Plan tab. It doesn't put it back on
          the calendar — those dates are long past — so schedule it again if you
          want it there.
        </p>
      </div>
    </>
  )
}

type ArchivedPlan = {
  id: number
  name: string
  days_per_week: number
  archived_at: string
}

function formatArchived(iso: string): string {
  const days = Math.floor((Date.now() - parseIso(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  return parseIso(iso).toLocaleDateString([], { month: 'short', year: 'numeric' })
}

/**
 * The exercises this user invented, and getting rid of the ones they regret.
 *
 * Only their own: the 93 canonical ones are shared by everybody, and removing
 * Back Squat from the library because you personally don't squat isn't a thing
 * to offer. The server enforces that too.
 *
 * Removal is not always deletion. An exercise you've logged against is
 * archived — hidden from the pickers, still present so history can name it —
 * because "stop offering me this" must never quietly rewrite what you did.
 * One you've never used is deleted outright.
 */
function YourExercises({ userId }: { userId: number }) {
  const exercises = useLiveQuery(() => db.exercises.toArray(), [], [] as Exercise[])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mine = exercises
    .filter((e) => e.owner_id === userId)
    .sort((a, b) => a.name.localeCompare(b.name))

  if (mine.length === 0) return null

  const act = async (exercise: Exercise, restore: boolean) => {
    setBusyId(exercise.id)
    setError(null)
    try {
      if (restore) {
        await api(`/exercises/${exercise.id}`, {
          method: 'PATCH', body: { archived: false },
        })
      } else {
        await api(`/exercises/${exercise.id}`, { method: 'DELETE' })
      }
      // Refetch rather than patching locally: the server decides whether that
      // was an archive or a delete, and the cache should reflect what it did.
      await refreshExercises()
    } catch {
      setError("Couldn't reach the server — that one needs a connection.")
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <h2>Your exercises</h2>
      <div className="card">
        <ul className="plain" style={{ margin: 0 }}>
          {mine.map((e) => (
            <li key={e.id}>
              <span>
                {e.name}
                {e.archived_at && <span className="muted"> · removed</span>}
              </span>
              <button
                className="secondary"
                disabled={busyId === e.id}
                style={{ marginTop: 0, padding: '.2rem .5rem', minHeight: 0,
                         fontSize: '.8rem' }}
                onClick={() => void act(e, Boolean(e.archived_at))}
              >
                {busyId === e.id ? '…' : e.archived_at ? 'Put back' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>

        {error && <div className="error" style={{ marginTop: '.5rem' }}>{error}</div>}

        <p className="muted" style={{ marginBottom: 0 }}>
          Removing one takes it out of the pickers. Anything you've already
          logged against it keeps its name in history, and you can put it back
          from here.
        </p>
      </div>
    </>
  )
}
